import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import Icon from '../components/Icon';
import PromoConfirmModal from '../components/PromoConfirmModal';
import CustomDatePicker from '../components/CustomDatePicker';
import {
  getNewsBanners,
  createNewsBanner,
  updateNewsBanner,
  deleteNewsBanner,
} from '../api';
import { formatDisplayDate } from '../utils/promoCodeUtils';
import {
  AUDIENCE_MODE,
  BANNER_ROLES,
  parseUserIds,
  findInvalidUserIds,
  resolveAudienceMode,
  sanitizeBannerHtml,
  htmlToPlainSnippet,
  isBannerLive,
} from '../utils/newsBannerUtils';

const DEFAULT_CREATE_FORM = {
  htmlContent: '',
  startsAt: '',
  expiresAt: '',
  targetRegisteredFrom: '',
  targetRegisteredTo: '',
  audience: AUDIENCE_MODE.ALL,
  userIds: '',
};

// Debounce a value so the live preview iframe only reloads after typing pauses,
// instead of flickering on every keystroke.
function useDebouncedValue(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

// Zoom bounds for the detailed preview modal.
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.25;

// Базовый размер «телефона» для превью — тот же, что в детальном просмотре,
// чтобы инлайн-превью и зум-модалка выглядели одинаково.
const PHONE_W = 390;
const PHONE_H = 800;

// Предпросмотр HTML-баннера в изолированном iframe: вставленный документ (его
// html/body/style) рендерится в песочнице и НЕ влияет на страницу админки.
// `onZoom` показывает кнопку для детального просмотра.
//
// `phone` = true: баннер рендерится в фиксированной рамке телефона (390×800) и
// масштабируется так, чтобы поместиться в бокс ЦЕЛИКОМ (как zoom-модалка).
// Иначе — обычный iframe во всю ширину (используется в карточках-миниатюрах).
const BannerPreview = ({ html, empty, title = 'banner-preview', onZoom, zoomLabel, phone = false }) => {
  const safe = useMemo(() => sanitizeBannerHtml(html), [html]);
  const stageRef = useRef(null);
  const [scale, setScale] = useState(0);

  useLayoutEffect(() => {
    if (!phone) return undefined;
    const el = stageRef.current;
    if (!el) return undefined;
    const compute = () => {
      const { width, height } = el.getBoundingClientRect();
      if (!width || !height) return;
      // Вписываем весь «телефон» в доступный бокс (letterbox).
      setScale(Math.min(width / PHONE_W, height / PHONE_H));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [phone]);

  const zoomBtn = onZoom && (
    <button
      type="button"
      className="admin-banner-zoom-btn"
      onClick={onZoom}
      title={zoomLabel}
      aria-label={zoomLabel}
    >
      <Icon name="search" size={15} />
    </button>
  );

  if (!safe.trim()) {
    return <div className="admin-banner-preview-empty">{empty}</div>;
  }

  if (phone) {
    return (
      <>
        <div className="admin-banner-phone-stage" ref={stageRef}>
          {scale > 0 && (
            <div
              className="admin-banner-phone-frame"
              style={{ width: PHONE_W, height: PHONE_H, transform: `scale(${scale})` }}
            >
              <iframe
                className="admin-banner-phone-iframe"
                title={title}
                srcDoc={safe}
                sandbox=""
              />
            </div>
          )}
        </div>
        {zoomBtn}
      </>
    );
  }

  return (
    <>
      <iframe
        className="admin-banner-preview-frame"
        title={title}
        srcDoc={safe}
        sandbox=""
      />
      {zoomBtn}
    </>
  );
};

// Детальный просмотр баннера с зумом. Рендерит тот же изолированный iframe в
// «телефонной» рамке и масштабирует его через CSS transform.
const BannerZoomModal = ({ target, onClose, t }) => {
  const [scale, setScale] = useState(1);
  const safe = useMemo(() => (target ? sanitizeBannerHtml(target.html) : ''), [target]);

  useEffect(() => {
    setScale(1);
  }, [target]);

  useEffect(() => {
    if (!target) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [target, onClose]);

  if (!target) return null;

  const clamp = (v) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(v * 100) / 100));
  const baseW = 390;
  const baseH = 800;

  // Rendered through a portal to <body> so the fixed navbar / animated tab
  // wrappers (which create their own stacking contexts) can't clip the modal
  // or push its toolbar underneath them.
  return createPortal(
    <div
      className="admin-modal-backdrop admin-banner-zoom-backdrop"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="admin-banner-zoom"
        role="dialog"
        aria-modal="true"
        aria-label={target.title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="admin-banner-zoom-toolbar">
          <span className="admin-banner-zoom-name">{target.title}</span>
          <div className="admin-banner-zoom-controls">
            <button
              type="button"
              className="admin-banner-zoom-ctrl"
              onClick={() => setScale((s) => clamp(s - ZOOM_STEP))}
              disabled={scale <= ZOOM_MIN}
              aria-label={t('admin.newsBanners.zoom.out')}
              title={t('admin.newsBanners.zoom.out')}
            >
              <Icon name="minus-circle" size={18} />
            </button>
            <button
              type="button"
              className="admin-banner-zoom-pct"
              onClick={() => setScale(1)}
              title={t('admin.newsBanners.zoom.reset')}
            >
              {Math.round(scale * 100)}%
            </button>
            <button
              type="button"
              className="admin-banner-zoom-ctrl"
              onClick={() => setScale((s) => clamp(s + ZOOM_STEP))}
              disabled={scale >= ZOOM_MAX}
              aria-label={t('admin.newsBanners.zoom.in')}
              title={t('admin.newsBanners.zoom.in')}
            >
              <Icon name="plus-circle" size={18} />
            </button>
            <button
              type="button"
              className="admin-banner-zoom-close"
              onClick={onClose}
              aria-label={t('admin.newsBanners.zoom.close')}
              title={t('admin.newsBanners.zoom.close')}
            >
              <Icon name="x" size={18} />
            </button>
          </div>
        </div>
        <div className="admin-banner-zoom-stage">
          <div
            className="admin-banner-zoom-sizer"
            style={{ width: baseW * scale, height: baseH * scale }}
          >
            <div
              className="admin-banner-zoom-frame-wrap"
              style={{ width: baseW, height: baseH, transform: `scale(${scale})` }}
            >
              <iframe
                className="admin-banner-zoom-frame"
                title={target.title}
                srcDoc={safe}
                sandbox=""
              />
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const AdminNewsBanners = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'ru';

  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);

  // ── Создание ──
  const [createForm, setCreateForm] = useState(DEFAULT_CREATE_FORM);
  const [createOpen, setCreateOpen] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createSuccess, setCreateSuccess] = useState(false);

  // ── Редактирование ──
  const [editTarget, setEditTarget] = useState(null); // баннер
  const [editForm, setEditForm] = useState(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState(null);

  // ── Удаление ──
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // ── Детальный просмотр (зум) ──  { html, title }
  const [zoomTarget, setZoomTarget] = useState(null);

  // Живое превью формы создания — с дебаунсом, чтобы iframe не мигал при вводе.
  const createPreviewHtml = useDebouncedValue(createForm.htmlContent, 300);

  // Стабильная ссылка на актуальный t, чтобы loadList не пересоздавался
  // (иначе смена t → новый loadList → лишний запрос списка).
  const tRef = useRef(t);
  tRef.current = t;

  const loadList = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const data = await getNewsBanners();
      setList(Array.isArray(data) ? data : []);
    } catch (e) {
      setListError(e?.message || tRef.current('admin.newsBanners.loadError'));
      setList([]);
    } finally {
      setListLoading(false);
    }
  }, []);

  // Начальная загрузка ровно один раз. Гард через ref гасит и повторный
  // вызов из-за двойного маунта React.StrictMode в dev-режиме.
  const didInitRef = useRef(false);
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    loadList();
  }, [loadList]);

  const audienceOptions = useMemo(
    () => [
      { key: AUDIENCE_MODE.ALL, icon: 'globe', label: t('admin.newsBanners.audience.all') },
      { key: AUDIENCE_MODE.CUSTOMER, icon: 'user', label: t('admin.newsBanners.audience.customer') },
      { key: AUDIENCE_MODE.PROVIDER, icon: 'briefcase', label: t('admin.newsBanners.audience.provider') },
      { key: AUDIENCE_MODE.USERS, icon: 'users', label: t('admin.newsBanners.audience.users') },
    ],
    [t]
  );

  const audienceLabel = useCallback(
    (banner) => {
      const mode = resolveAudienceMode(banner);
      if (mode === AUDIENCE_MODE.USERS) {
        return t('admin.newsBanners.audience.usersCount', { count: banner.targetUserIds.length });
      }
      if (mode === AUDIENCE_MODE.CUSTOMER) return t('admin.newsBanners.audience.customer');
      if (mode === AUDIENCE_MODE.PROVIDER) return t('admin.newsBanners.audience.provider');
      return t('admin.newsBanners.audience.all');
    },
    [t]
  );

  const audienceBadgeClass = (banner) => {
    const mode = resolveAudienceMode(banner);
    if (mode === AUDIENCE_MODE.USERS) return 'users';
    if (mode === AUDIENCE_MODE.CUSTOMER) return 'customer';
    if (mode === AUDIENCE_MODE.PROVIDER) return 'provider';
    return 'all';
  };

  // ── Создание баннера ──
  const buildCreatePayload = () => {
    const htmlContent = createForm.htmlContent.trim();
    if (!htmlContent) {
      setCreateError(t('admin.newsBanners.errorHtml'));
      return null;
    }

    const startsAt = createForm.startsAt || null;
    const expiresAt = createForm.expiresAt || null;
    if (startsAt && expiresAt && new Date(expiresAt) <= new Date(startsAt)) {
      setCreateError(t('admin.newsBanners.errorDateOrder'));
      return null;
    }

    const targetRegisteredFrom = createForm.targetRegisteredFrom || null;
    const targetRegisteredTo = createForm.targetRegisteredTo || null;
    if (
      targetRegisteredFrom &&
      targetRegisteredTo &&
      new Date(targetRegisteredTo) <= new Date(targetRegisteredFrom)
    ) {
      setCreateError(t('admin.newsBanners.errorRegisteredDateOrder'));
      return null;
    }

    const payload = { htmlContent };
    if (startsAt) payload.startsAt = startsAt;
    if (expiresAt) payload.expiresAt = expiresAt;
    if (targetRegisteredFrom) payload.targetRegisteredFrom = targetRegisteredFrom;
    if (targetRegisteredTo) payload.targetRegisteredTo = targetRegisteredTo;

    if (createForm.audience === AUDIENCE_MODE.USERS) {
      const ids = parseUserIds(createForm.userIds);
      if (ids.length === 0) {
        setCreateError(t('admin.newsBanners.errorUsersRequired'));
        return null;
      }
      const invalid = findInvalidUserIds(ids);
      if (invalid.length > 0) {
        setCreateError(t('admin.newsBanners.errorUsersInvalid', { ids: invalid.join(', ') }));
        return null;
      }
      payload.targetUserIds = ids;
    } else if (createForm.audience === AUDIENCE_MODE.CUSTOMER) {
      payload.targetRole = BANNER_ROLES.CUSTOMER;
    } else if (createForm.audience === AUDIENCE_MODE.PROVIDER) {
      payload.targetRole = BANNER_ROLES.PROVIDER;
    }
    return payload;
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setCreateError(null);
    setCreateSuccess(false);

    const payload = buildCreatePayload();
    if (!payload) return;

    setCreateSubmitting(true);
    try {
      await createNewsBanner(payload);
      setCreateSuccess(true);
      setCreateForm(DEFAULT_CREATE_FORM);
      setCreateOpen(false);
      await loadList();
      window.setTimeout(() => setCreateSuccess(false), 5000);
    } catch (err) {
      setCreateError(err?.message || t('admin.newsBanners.createError'));
    } finally {
      setCreateSubmitting(false);
    }
  };

  // ── Редактирование баннера ──
  const openEdit = (banner) => {
    setEditError(null);
    setEditTarget(banner);
    setEditForm({
      startsAt: banner.startsAt || '',
      expiresAt: banner.expiresAt || '',
      targetRegisteredFrom: banner.targetRegisteredFrom || '',
      targetRegisteredTo: banner.targetRegisteredTo || '',
      audience: resolveAudienceMode(banner),
      userIds: Array.isArray(banner.targetUserIds) ? banner.targetUserIds.join(', ') : '',
    });
  };

  const closeEdit = () => {
    if (editSubmitting) return;
    setEditTarget(null);
    setEditForm(null);
    setEditError(null);
  };

  const handleEditSubmit = async () => {
    if (!editTarget || !editForm) return;
    setEditError(null);

    const startsAt = editForm.startsAt || null;
    const expiresAt = editForm.expiresAt || null;
    if (startsAt && expiresAt && new Date(expiresAt) <= new Date(startsAt)) {
      setEditError(t('admin.newsBanners.errorDateOrder'));
      return;
    }

    const targetRegisteredFrom = editForm.targetRegisteredFrom || null;
    const targetRegisteredTo = editForm.targetRegisteredTo || null;
    if (
      targetRegisteredFrom &&
      targetRegisteredTo &&
      new Date(targetRegisteredTo) <= new Date(targetRegisteredFrom)
    ) {
      setEditError(t('admin.newsBanners.errorRegisteredDateOrder'));
      return;
    }

    const payload = {
      startsAt: startsAt || null,
      expiresAt: expiresAt || null,
      targetRegisteredFrom: targetRegisteredFrom || null,
      targetRegisteredTo: targetRegisteredTo || null,
    };

    if (editForm.audience === AUDIENCE_MODE.USERS) {
      const ids = parseUserIds(editForm.userIds);
      if (ids.length === 0) {
        setEditError(t('admin.newsBanners.errorUsersRequired'));
        return;
      }
      const invalid = findInvalidUserIds(ids);
      if (invalid.length > 0) {
        setEditError(t('admin.newsBanners.errorUsersInvalid', { ids: invalid.join(', ') }));
        return;
      }
      payload.targetUserIds = ids;
    } else {
      // Любой role-based режим сбрасывает адресный список.
      payload.targetUserIds = [];
      if (editForm.audience === AUDIENCE_MODE.ALL) {
        payload.clearTargetRole = true;
      } else if (editForm.audience === AUDIENCE_MODE.CUSTOMER) {
        payload.targetRole = BANNER_ROLES.CUSTOMER;
      } else if (editForm.audience === AUDIENCE_MODE.PROVIDER) {
        payload.targetRole = BANNER_ROLES.PROVIDER;
      }
    }

    setEditSubmitting(true);
    try {
      await updateNewsBanner(editTarget.id, payload);
      await loadList();
      setEditTarget(null);
      setEditForm(null);
    } catch (err) {
      setEditError(err?.message || t('admin.newsBanners.updateError'));
    } finally {
      setEditSubmitting(false);
    }
  };

  // ── Удаление баннера ──
  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteError(null);
    setDeleteSubmitting(true);
    try {
      await deleteNewsBanner(deleteTarget.id);
      await loadList();
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError(err?.message || t('admin.newsBanners.deleteError'));
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const renderAudiencePicker = (value, onChange) => (
    <div className="admin-banner-audience-grid">
      {audienceOptions.map((opt) => (
        <button
          key={opt.key}
          type="button"
          className={`admin-banner-audience-pill ${value === opt.key ? 'active' : ''}`}
          onClick={() => onChange(opt.key)}
        >
          <Icon name={opt.icon} size={15} />
          <span>{opt.label}</span>
        </button>
      ))}
    </div>
  );

  return (
    <div className="admin-promo-codes admin-promo-list-page admin-news-banners">
      <div className="admin-promo-list-shell">
        <header className="admin-promo-list-header">
          <div className="admin-promo-list-header-text">
            <h3 className="admin-promo-list-title">
              <span className="admin-promo-list-title-icon admin-banner-title-icon">
                <Icon name="image" size={18} />
              </span>
              {t('admin.newsBanners.title')}
            </h3>
            <p className="admin-promo-list-subtitle">{t('admin.newsBanners.listSubtitle')}</p>
          </div>
          <div className="admin-promo-header-actions">
            <button
              type="button"
              className="btn-premium-action-new admin-promo-refresh-btn"
              onClick={loadList}
              disabled={listLoading}
              title={t('admin.newsBanners.refresh')}
            >
              <Icon name="refresh" size={16} />
            </button>
            <button
              type="button"
              className="btn-premium-action-new"
              onClick={() => {
                setCreateOpen((v) => !v);
                setCreateError(null);
              }}
            >
              <Icon name={createOpen ? 'x' : 'plus-circle'} size={16} />
              <span>{createOpen ? t('admin.newsBanners.cancelCreate') : t('admin.newsBanners.create')}</span>
            </button>
          </div>
        </header>

        {createSuccess && (
          <div className="form-status success admin-promo-banner" role="status">
            {t('admin.newsBanners.createSuccess')}
          </div>
        )}

        <div className={`admin-promo-create-collapse${createOpen ? ' is-open' : ''}`} aria-hidden={!createOpen}>
          <div className="admin-promo-create-collapse-inner">
            <form className="admin-promo-create-panel" onSubmit={handleCreateSubmit}>
              <div className="admin-promo-create-panel-inner admin-banner-create-inner">
                <h4 className="admin-promo-create-panel-title">{t('admin.newsBanners.create')}</h4>

                <div className="admin-banner-editor-grid">
                  <label className="admin-promo-field admin-banner-html-field">
                    <span className="admin-promo-field-label">{t('admin.newsBanners.htmlContent')}</span>
                    <textarea
                      className="premium-input-minimal admin-banner-html-input"
                      value={createForm.htmlContent}
                      onChange={(e) => setCreateForm((p) => ({ ...p, htmlContent: e.target.value }))}
                      placeholder={t('admin.newsBanners.htmlPlaceholder')}
                      rows={8}
                      spellCheck={false}
                      required
                    />
                  </label>

                  <div className="admin-banner-preview-field">
                    <span className="admin-promo-field-label">{t('admin.newsBanners.preview')}</span>
                    <div className="admin-banner-preview-box">
                      <BannerPreview
                        html={createPreviewHtml}
                        empty={t('admin.newsBanners.previewEmpty')}
                        title={t('admin.newsBanners.preview')}
                        zoomLabel={t('admin.newsBanners.zoom.open')}
                        phone
                        onZoom={() =>
                          setZoomTarget({
                            html: createForm.htmlContent,
                            title: t('admin.newsBanners.preview'),
                          })
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="admin-promo-create-grid admin-banner-dates-grid">
                  <label className="admin-promo-field">
                    <span className="admin-promo-field-label">
                      {t('admin.newsBanners.startsAt')}{' '}
                      <span className="admin-form-optional">{t('admin.form.optional')}</span>
                    </span>
                    <CustomDatePicker
                      selected={createForm.startsAt ? new Date(createForm.startsAt) : null}
                      onChange={(date) =>
                        setCreateForm((p) => ({ ...p, startsAt: date ? date.toISOString() : '' }))
                      }
                    />
                  </label>
                  <label className="admin-promo-field">
                    <span className="admin-promo-field-label">
                      {t('admin.newsBanners.expiresAt')}{' '}
                      <span className="admin-form-optional">{t('admin.form.optional')}</span>
                    </span>
                    <CustomDatePicker
                      selected={createForm.expiresAt ? new Date(createForm.expiresAt) : null}
                      onChange={(date) =>
                        setCreateForm((p) => ({ ...p, expiresAt: date ? date.toISOString() : '' }))
                      }
                    />
                  </label>
                </div>

                <div className="admin-promo-create-grid admin-banner-dates-grid">
                  <label className="admin-promo-field">
                    <span className="admin-promo-field-label">
                      {t('admin.newsBanners.registeredFrom')}{' '}
                      <span className="admin-form-optional">{t('admin.form.optional')}</span>
                    </span>
                    <CustomDatePicker
                      selected={createForm.targetRegisteredFrom ? new Date(createForm.targetRegisteredFrom) : null}
                      onChange={(date) =>
                        setCreateForm((p) => ({ ...p, targetRegisteredFrom: date ? date.toISOString() : '' }))
                      }
                    />
                  </label>
                  <label className="admin-promo-field">
                    <span className="admin-promo-field-label">
                      {t('admin.newsBanners.registeredTo')}{' '}
                      <span className="admin-form-optional">{t('admin.form.optional')}</span>
                    </span>
                    <CustomDatePicker
                      selected={createForm.targetRegisteredTo ? new Date(createForm.targetRegisteredTo) : null}
                      onChange={(date) =>
                        setCreateForm((p) => ({ ...p, targetRegisteredTo: date ? date.toISOString() : '' }))
                      }
                    />
                  </label>
                  <span className="admin-banner-hint admin-banner-registered-hint">
                    {t('admin.newsBanners.registeredHint')}
                  </span>
                </div>

                <div className="admin-promo-field admin-banner-audience-field">
                  <span className="admin-promo-field-label">{t('admin.newsBanners.audienceLabel')}</span>
                  {renderAudiencePicker(createForm.audience, (val) =>
                    setCreateForm((p) => ({ ...p, audience: val }))
                  )}
                  <AnimatePresence initial={false}>
                    {createForm.audience === AUDIENCE_MODE.USERS && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ overflow: 'hidden' }}
                      >
                        <textarea
                          className="premium-input-minimal admin-banner-ids-input"
                          value={createForm.userIds}
                          onChange={(e) => setCreateForm((p) => ({ ...p, userIds: e.target.value }))}
                          placeholder={t('admin.newsBanners.userIdsPlaceholder')}
                          rows={3}
                          spellCheck={false}
                        />
                        <span className="admin-banner-hint">{t('admin.newsBanners.userIdsHint')}</span>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {createError && (
                  <div className="form-status error" role="alert">
                    {createError}
                  </div>
                )}
                <div className="admin-promo-create-actions">
                  <button type="submit" className="btn-premium-action-new" disabled={createSubmitting}>
                    <Icon name="check" size={16} />
                    <span>{createSubmitting ? t('admin.newsBanners.submitting') : t('admin.newsBanners.submit')}</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

        <div className="admin-promo-toolbar">
          <span className="admin-promo-results-count">
            {t('admin.newsBanners.totalCount', { count: list.length })}
          </span>
        </div>

        <div className="admin-promo-list-body">
          {listLoading && <p className="admin-promo-muted admin-promo-list-empty">{t('common.loading')}</p>}
          {listError && !listLoading && (
            <div className="form-status error" role="alert">
              {listError}
            </div>
          )}
          {!listLoading && !listError && list.length === 0 && (
            <div className="admin-promo-empty-state">
              <Icon name="image" size={40} />
              <p>{t('admin.newsBanners.empty')}</p>
              <button type="button" className="btn-premium-action-new" onClick={() => setCreateOpen(true)}>
                <Icon name="plus-circle" size={16} />
                <span>{t('admin.newsBanners.create')}</span>
              </button>
            </div>
          )}
          {!listLoading && list.length > 0 && (
            <div className="admin-banner-cards">
              {list.map((banner) => {
                const live = isBannerLive(banner);
                return (
                  <article key={banner.id} className="admin-banner-card">
                    <div className="admin-banner-card-preview">
                      <BannerPreview
                        html={banner.htmlContent}
                        empty={t('admin.newsBanners.previewEmpty')}
                        title={audienceLabel(banner)}
                        zoomLabel={t('admin.newsBanners.zoom.open')}
                        onZoom={() =>
                          setZoomTarget({
                            html: banner.htmlContent,
                            title: audienceLabel(banner),
                          })
                        }
                      />
                    </div>
                    <div className="admin-banner-card-body">
                      <div className="admin-banner-card-top">
                        <span className={`admin-banner-badge ${audienceBadgeClass(banner)}`}>
                          {audienceLabel(banner)}
                        </span>
                        <span className={`admin-banner-live-dot ${live ? 'live' : 'idle'}`}>
                          <span className="dot" />
                          {live ? t('admin.newsBanners.statusLive') : t('admin.newsBanners.statusIdle')}
                        </span>
                      </div>
                      <p className="admin-banner-card-snippet">
                        {htmlToPlainSnippet(banner.htmlContent) || t('admin.newsBanners.noText')}
                      </p>
                      <div className="admin-banner-card-meta">
                        <span className="admin-banner-meta-item">
                          <Icon name="calendar" size={13} />
                          {t('admin.newsBanners.startsAt')}: {formatDisplayDate(banner.startsAt, locale)}
                        </span>
                        <span className="admin-banner-meta-item">
                          <Icon name="clock" size={13} />
                          {t('admin.newsBanners.expiresAt')}: {formatDisplayDate(banner.expiresAt, locale)}
                        </span>
                        {(banner.targetRegisteredFrom || banner.targetRegisteredTo) && (
                          <span className="admin-banner-meta-item">
                            <Icon name="user-plus" size={13} />
                            {t('admin.newsBanners.registeredFrom')}:{' '}
                            {formatDisplayDate(banner.targetRegisteredFrom, locale)}
                            {banner.targetRegisteredTo
                              ? ` — ${formatDisplayDate(banner.targetRegisteredTo, locale)}`
                              : ''}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="admin-banner-card-actions">
                      <button
                        type="button"
                        className="admin-banner-icon-btn"
                        onClick={() => openEdit(banner)}
                        title={t('admin.newsBanners.edit')}
                        aria-label={t('admin.newsBanners.edit')}
                      >
                        <Icon name="pencil" size={16} />
                      </button>
                      <button
                        type="button"
                        className="admin-banner-icon-btn danger"
                        onClick={() => {
                          setDeleteError(null);
                          setDeleteTarget(banner);
                        }}
                        title={t('admin.newsBanners.delete')}
                        aria-label={t('admin.newsBanners.delete')}
                      >
                        <Icon name="trash-2" size={16} />
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Модалка редактирования */}
      {editTarget && editForm && createPortal(
        <div className="admin-modal-backdrop" onClick={closeEdit} role="presentation">
          <div
            className="admin-modal admin-banner-edit-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="banner-edit-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="banner-edit-title" className="admin-promo-confirm-title">
                {t('admin.newsBanners.editTitle')}
              </h3>
            </div>
            <div className="modal-body admin-banner-edit-body">
              <p className="admin-banner-edit-note">{t('admin.newsBanners.editNote')}</p>

              <div className="admin-banner-preview-box admin-banner-preview-box--modal">
                <BannerPreview
                  html={editTarget.htmlContent}
                  empty={t('admin.newsBanners.previewEmpty')}
                  title={t('admin.newsBanners.editTitle')}
                  zoomLabel={t('admin.newsBanners.zoom.open')}
                  phone
                  onZoom={() =>
                    setZoomTarget({
                      html: editTarget.htmlContent,
                      title: t('admin.newsBanners.editTitle'),
                    })
                  }
                />
              </div>

              <div className="admin-promo-create-grid admin-banner-dates-grid">
                <label className="admin-promo-field">
                  <span className="admin-promo-field-label">
                    {t('admin.newsBanners.startsAt')}{' '}
                    <span className="admin-form-optional">{t('admin.form.optional')}</span>
                  </span>
                  <CustomDatePicker
                    selected={editForm.startsAt ? new Date(editForm.startsAt) : null}
                    onChange={(date) =>
                      setEditForm((p) => ({ ...p, startsAt: date ? date.toISOString() : '' }))
                    }
                  />
                </label>
                <label className="admin-promo-field">
                  <span className="admin-promo-field-label">
                    {t('admin.newsBanners.expiresAt')}{' '}
                    <span className="admin-form-optional">{t('admin.form.optional')}</span>
                  </span>
                  <CustomDatePicker
                    selected={editForm.expiresAt ? new Date(editForm.expiresAt) : null}
                    onChange={(date) =>
                      setEditForm((p) => ({ ...p, expiresAt: date ? date.toISOString() : '' }))
                    }
                  />
                </label>
              </div>

              <div className="admin-promo-create-grid admin-banner-dates-grid">
                <label className="admin-promo-field">
                  <span className="admin-promo-field-label">
                    {t('admin.newsBanners.registeredFrom')}{' '}
                    <span className="admin-form-optional">{t('admin.form.optional')}</span>
                  </span>
                  <CustomDatePicker
                    selected={editForm.targetRegisteredFrom ? new Date(editForm.targetRegisteredFrom) : null}
                    onChange={(date) =>
                      setEditForm((p) => ({ ...p, targetRegisteredFrom: date ? date.toISOString() : '' }))
                    }
                  />
                </label>
                <label className="admin-promo-field">
                  <span className="admin-promo-field-label">
                    {t('admin.newsBanners.registeredTo')}{' '}
                    <span className="admin-form-optional">{t('admin.form.optional')}</span>
                  </span>
                  <CustomDatePicker
                    selected={editForm.targetRegisteredTo ? new Date(editForm.targetRegisteredTo) : null}
                    onChange={(date) =>
                      setEditForm((p) => ({ ...p, targetRegisteredTo: date ? date.toISOString() : '' }))
                    }
                  />
                </label>
                <span className="admin-banner-hint admin-banner-registered-hint">
                  {t('admin.newsBanners.registeredHint')}
                </span>
              </div>

              <div className="admin-promo-field admin-banner-audience-field">
                <span className="admin-promo-field-label">{t('admin.newsBanners.audienceLabel')}</span>
                {renderAudiencePicker(editForm.audience, (val) =>
                  setEditForm((p) => ({ ...p, audience: val }))
                )}
                <AnimatePresence initial={false}>
                  {editForm.audience === AUDIENCE_MODE.USERS && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      style={{ overflow: 'hidden' }}
                    >
                      <textarea
                        className="premium-input-minimal admin-banner-ids-input"
                        value={editForm.userIds}
                        onChange={(e) => setEditForm((p) => ({ ...p, userIds: e.target.value }))}
                        placeholder={t('admin.newsBanners.userIdsPlaceholder')}
                        rows={3}
                        spellCheck={false}
                      />
                      <span className="admin-banner-hint">{t('admin.newsBanners.userIdsHint')}</span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {editError && (
                <div className="form-status error" role="alert">
                  {editError}
                </div>
              )}
            </div>
            <div className="modal-actions admin-modal-actions">
              <button
                type="button"
                className="btn btn-secondary admin-modal-btn-cancel"
                onClick={closeEdit}
                disabled={editSubmitting}
              >
                {t('admin.newsBanners.confirm.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary admin-modal-btn-submit"
                onClick={handleEditSubmit}
                disabled={editSubmitting}
              >
                {editSubmitting ? t('admin.newsBanners.submitting') : t('admin.newsBanners.saveChanges')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Подтверждение удаления */}
      <PromoConfirmModal
        open={!!deleteTarget}
        title={t('admin.newsBanners.confirm.deleteTitle')}
        subtitle={t('admin.newsBanners.confirm.deleteSubtitle')}
        items={
          deleteTarget
            ? [
                {
                  label: t('admin.newsBanners.audienceLabel'),
                  value: audienceLabel(deleteTarget),
                },
                {
                  label: t('admin.newsBanners.startsAt'),
                  value: deleteTarget.startsAt
                    ? formatDisplayDate(deleteTarget.startsAt, locale)
                    : t('admin.newsBanners.confirm.notSpecified'),
                },
                {
                  label: t('admin.newsBanners.expiresAt'),
                  value: deleteTarget.expiresAt
                    ? formatDisplayDate(deleteTarget.expiresAt, locale)
                    : t('admin.newsBanners.confirm.notSpecified'),
                },
              ]
            : []
        }
        error={deleteError}
        submitting={deleteSubmitting}
        onCancel={() => {
          if (deleteSubmitting) return;
          setDeleteTarget(null);
          setDeleteError(null);
        }}
        onConfirm={handleConfirmDelete}
        cancelLabel={t('admin.newsBanners.confirm.cancel')}
        confirmLabel={deleteSubmitting ? t('admin.newsBanners.deleting') : t('admin.newsBanners.confirm.delete')}
        titleId="banner-delete-confirm-title"
      />

      {/* Детальный просмотр с зумом */}
      <BannerZoomModal target={zoomTarget} onClose={() => setZoomTarget(null)} t={t} />
    </div>
  );
};

export default AdminNewsBanners;
